/**
 * Receipt attach / view / replace for one record (expense, card bill payment).
 * `fetchUrl()` resolves { downloadUrl }; `upload(file)` stores the file. Images or PDF, up to 10 MB.
 */

import React, { useRef, useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { AttachFile, Visibility, UploadFile } from '@mui/icons-material';
import { serverMessage } from '../../utils/apiConvert';

const MAX_BYTES = 10 * 1024 * 1024;

const ReceiptAttachment = ({ hasReceipt, filename, fetchUrl, upload, canEdit = true, onUploaded, onError, compact = false }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const view = async () => {
    setBusy(true);
    try {
      const r = await fetchUrl();
      if (r?.downloadUrl) window.open(r.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      onError?.(serverMessage(err, 'Could not open the receipt'));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!(file.type.startsWith('image/') || file.type === 'application/pdf')) {
      onError?.('The receipt must be an image or a PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.('The receipt file exceeds 10 MB');
      return;
    }
    setBusy(true);
    try {
      const result = await upload(file);
      onUploaded?.(result);
    } catch (err) {
      onError?.(serverMessage(err, 'Could not upload the receipt'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {!compact && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <AttachFile fontSize="small" color={hasReceipt ? 'primary' : 'disabled'} />
          <Typography variant="body2" color={hasReceipt ? 'text.primary' : 'text.secondary'}>
            {hasReceipt ? (filename || 'Receipt attached') : 'No receipt'}
          </Typography>
        </Box>
      )}
      {busy && <CircularProgress size={16} />}
      {hasReceipt && (
        <Button size="small" startIcon={<Visibility />} onClick={view} disabled={busy}>View</Button>
      )}
      {canEdit && (
        <Button size="small" startIcon={<UploadFile />} onClick={() => input.current?.click()} disabled={busy}>
          {hasReceipt ? 'Replace' : 'Attach'}
        </Button>
      )}
      <input ref={input} type="file" accept="image/*,application/pdf" hidden onChange={pick} />
    </Box>
  );
};

export default ReceiptAttachment;
